import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import classNames from "classnames";
import type { LucideIcon } from "lucide-react";
import {
  Clock3,
  Compass,
  FileQuestion,
  HelpCircle,
  Settings,
  Shapes,
  Users,
} from "lucide-react";
import { memo, useCallback, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOnboardingTracking } from "../../hooks/use-onboarding-tracking";
import { useProduct } from "../../hooks/use-product";
import { OnboardingV2RpcClientContext } from "../../rpc-client";
import * as stepShellStyles from "../layouts/step-shell.module.css";
import { AiStarIcon } from "./ai-star-icon";
import { DecryptedText } from "./decrypted-text";
import * as styles from "./exit-onboarding-button.module.css";

const DEFAULT_MODAL_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/onboarding-v2-returns.webp";
const FEEDBACK_FORM_KEY = "exit-feedback";

interface ExitReasonOption {
  id: string;
  label: string;
  Icon: LucideIcon;
  hidden: boolean;
}

export const EXIT_REASON_OPTIONS = [
  {
    id: "need-more-time",
    label: "I need more time",
    Icon: Clock3,
    hidden: false,
  },
  {
    id: "need-more-information",
    label: "I'm still gathering information",
    Icon: FileQuestion,
    hidden: false,
  },
  {
    id: "need-approval",
    label: "I need approval from my team",
    Icon: Users,
    hidden: false,
  },
  {
    id: "need-help",
    label: "I want help from Redo",
    Icon: HelpCircle,
    hidden: false,
  },
  {
    id: "need-rma-help",
    label: "I need help with my RMA setup",
    Icon: Settings,
    hidden: true,
  },
  {
    id: "just-exploring",
    label: "I'm just exploring for now",
    Icon: Compass,
    hidden: false,
  },
  { id: "other", label: "Other", Icon: Shapes, hidden: false },
] as const satisfies readonly ExitReasonOption[];

export type ExitReasonId = (typeof EXIT_REASON_OPTIONS)[number]["id"];

const NEED_HELP_REASON_ID = "need-help" satisfies ExitReasonId;
const NEED_RMA_HELP_REASON_ID = "need-rma-help" satisfies ExitReasonId;
const SCHEDULE_CALL_REASON_IDS = new Set<ExitReasonId>([
  NEED_HELP_REASON_ID,
  NEED_RMA_HELP_REASON_ID,
]);

const EXIT_REASON_FOLLOW_UP: Record<ExitReasonId, { placeholder: string }> = {
  "need-more-time": {
    placeholder: "Anything that would help when you come back",
  },
  "need-more-information": {
    placeholder: "What you still need to find or confirm",
  },
  "need-approval": { placeholder: "Who or what you're waiting on" },
  "need-help": { placeholder: "What you want help with" },
  "need-rma-help": { placeholder: "Describe your system and what you need" },
  "just-exploring": { placeholder: "What you're still weighing" },
  other: { placeholder: "Anything we should know" },
};

export const FeedbackModal = memo(function FeedbackModal({
  isOpen,
  onClose,
  onConfirm,
  initialReasonId,
  hideAskRedo,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  initialReasonId?: ExitReasonId;
  hideAskRedo?: boolean;
}) {
  const rpcClient = useContext(OnboardingV2RpcClientContext);
  const product = useProduct();
  const navigate = useNavigate();
  const { open: openAiPanel } = useAiSlidePanel();
  const { stepPath } = useParams<{ stepPath: string }>();
  const { trackExitReason } = useOnboardingTracking();

  const startAtFollowup = !!initialReasonId;
  const [isSaving, setIsSaving] = useState(false);
  const [modalStep, setModalStep] = useState<"reason-select" | "followup">(
    startAtFollowup ? "followup" : "reason-select",
  );
  const [selectedReasonId, setSelectedReasonId] = useState<ExitReasonId | null>(
    initialReasonId ?? null,
  );
  const [followUpResponse, setFollowUpResponse] = useState("");

  const resetState = useCallback(() => {
    setIsSaving(false);
    setModalStep(startAtFollowup ? "followup" : "reason-select");
    setSelectedReasonId(initialReasonId ?? null);
    setFollowUpResponse("");
  }, [startAtFollowup, initialReasonId]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleContinue = useCallback(() => {
    if (!selectedReasonId) return;
    setModalStep("followup");
  }, [selectedReasonId]);

  const handleBack = useCallback(() => {
    if (startAtFollowup) {
      handleClose();
    } else {
      resetState();
    }
  }, [startAtFollowup, handleClose, resetState]);

  const selectedReason = selectedReasonId
    ? (EXIT_REASON_OPTIONS.find((option) => option.id === selectedReasonId) ??
      null)
    : null;
  const followUpConfig = selectedReason
    ? EXIT_REASON_FOLLOW_UP[selectedReason.id]
    : null;
  const currentStep = product?.steps.find((step) => step.path === stepPath);
  const headerImageUrl = product?.imageUrl ?? DEFAULT_MODAL_IMAGE_URL;
  const showFollowUpStep =
    modalStep === "followup" && !!selectedReason && !!followUpConfig;
  const showNeedHelpFollowUp =
    showFollowUpStep &&
    !!selectedReason &&
    SCHEDULE_CALL_REASON_IDS.has(selectedReason.id);

  const handleConfirm = useCallback(async () => {
    const trimmedResponse = followUpResponse.trim();
    if (!trimmedResponse || !selectedReason) return;

    const wantsScheduleCall = SCHEDULE_CALL_REASON_IDS.has(selectedReason.id);
    const scheduleCallPath =
      product && stepPath ? `/${product.id}/schedule-call` : null;

    setIsSaving(true);
    try {
      if (rpcClient && product && currentStep) {
        const stepIndex = product.steps.findIndex(
          (s) => s.id === currentStep.id,
        );
        trackExitReason({
          stepId: currentStep.id,
          stepIndex,
          reasonId: selectedReason.id,
          reasonLabel: selectedReason.label,
          responseText: trimmedResponse,
        });
        await rpcClient.saveProgress({
          productId: product.id,
          currentStepId: currentStep.id,
          sourceStepId: FEEDBACK_FORM_KEY,
          status: wantsScheduleCall ? "help-requested" : undefined,
          stepData: {
            reasonId: selectedReason.id,
            reasonLabel: selectedReason.label,
            responseText: trimmedResponse,
            stepId: currentStep.id,
            submittedAt: new Date().toISOString(),
          },
        });
      }
    } catch {
      // Do not block the flow if feedback persistence fails.
    } finally {
      setIsSaving(false);
      resetState();
      if (wantsScheduleCall && scheduleCallPath && product && stepPath) {
        onClose();
        navigate(scheduleCallPath, {
          state: { from: `/${product.id}/${stepPath}` },
        });
      } else {
        onConfirm();
      }
    }
  }, [
    currentStep,
    followUpResponse,
    navigate,
    product,
    rpcClient,
    selectedReason,
    stepPath,
    resetState,
    onClose,
    onConfirm,
    trackExitReason,
  ]);

  return (
    <RedoModal
      hideFooter
      hideHeader
      isOpen={isOpen}
      modalSize="sm"
      muiClassName={styles.exitModalRoot}
      noPadding
      onModalCloseRequested={handleClose}
      title="Before you go"
    >
      <div className={styles.modalImageWrapper}>
        <img alt="" className={styles.modalHeaderImage} src={headerImageUrl} />
        <div className={styles.modalImageOverlay} />
        <div className={styles.modalImageLabel}>
          <span className={styles.modalImageTitle}>
            <DecryptedText
              animateOn="view"
              key={
                showFollowUpStep && selectedReason
                  ? selectedReason.label
                  : "Before you go"
              }
              sequential
              speed={40}
              text={
                showFollowUpStep && selectedReason
                  ? selectedReason.label
                  : "Before you go"
              }
            />
          </span>
          {!showFollowUpStep && (
            <p className={styles.modalImageSubheading}>
              Choose the main reason you're leaving onboarding right now.
            </p>
          )}
        </div>
      </div>

      <div className={styles.modalContentBody}>
        {showFollowUpStep && selectedReason && followUpConfig ? (
          <RedoTextareaInputField
            autoFocus
            className={styles.followUpInput}
            containerHeight="120px"
            fullWidth
            label="Tell us more"
            placeholder={followUpConfig.placeholder}
            setValue={setFollowUpResponse}
            value={followUpResponse}
          />
        ) : (
          <div className={styles.reasonList}>
            {EXIT_REASON_OPTIONS.filter((o) => !o.hidden).map((option) => (
              <button
                className={classNames(
                  styles.reasonCard,
                  selectedReasonId === option.id && styles.reasonCardSelected,
                )}
                key={option.id}
                onClick={() => setSelectedReasonId(option.id)}
                type="button"
              >
                <div className={styles.reasonLabel}>
                  <div className={styles.reasonIconWrap}>
                    <option.Icon className={styles.reasonIcon} />
                  </div>
                  <span className={styles.reasonTitle}>{option.label}</span>
                </div>
                <div
                  className={classNames(
                    styles.reasonIndicator,
                    selectedReasonId === option.id &&
                      styles.reasonIndicatorSelected,
                  )}
                >
                  <div className={styles.reasonIndicatorDot} />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className={styles.actionsRow}>
          <button
            className={classNames(
              stepShellStyles.continueButton,
              styles.exitModalActionBtn,
            )}
            disabled={isSaving}
            onClick={showFollowUpStep ? handleBack : handleClose}
            type="button"
          >
            {showFollowUpStep
              ? startAtFollowup
                ? "Cancel"
                : "Back"
              : "Stay here"}
          </button>
          {showFollowUpStep ? (
            <div className={styles.actionsRowEnd}>
              {!hideAskRedo && (
                <button
                  className={styles.askRedoBlackBtn}
                  disabled={isSaving}
                  onClick={() => {
                    resetState();
                    onClose();
                    openAiPanel({
                      prefillInput: followUpResponse.trim() || undefined,
                      autoSubmit: false,
                    });
                  }}
                  type="button"
                >
                  <AiStarIcon className={styles.askRedoBlackBtnIcon} />
                  Ask Redo
                </button>
              )}
              <button
                className={classNames(
                  stepShellStyles.continueButton,
                  styles.exitModalActionBtn,
                )}
                disabled={isSaving || followUpResponse.trim().length === 0}
                onClick={handleConfirm}
                type="button"
              >
                {isSaving
                  ? showNeedHelpFollowUp
                    ? "Saving..."
                    : "Exiting..."
                  : showNeedHelpFollowUp
                    ? "Schedule a Call"
                    : "Exit to Dashboard"}
              </button>
            </div>
          ) : (
            <button
              className={classNames(
                stepShellStyles.continueButton,
                styles.exitModalActionBtn,
              )}
              disabled={isSaving || !selectedReasonId}
              onClick={handleContinue}
              type="button"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </RedoModal>
  );
});
